import { useLocalStorage } from '@mantine/hooks'
import { compact, round } from 'lodash-es'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { isMatching, match, P } from 'ts-pattern'
import { withQuery } from 'ufo'
import { openCancelSubscriptionDialog } from 'src/components/Membership/dialogs'
import { UserCancelled } from 'src/components/Modal/Confirm'
import { openPaymentDialog } from 'src/components/Payment/PaymentDialog'
import { useCurrency } from 'src/utils/currency'
import dayjs from 'src/utils/dayjs'
import { missingArgumentError } from 'src/utils/error'
import { useMatches } from 'src/utils/router/route'
import { endEventGroup, track } from 'src/utils/tracker'
import { UserSubscriptionStatus } from 'src/utils/user'
import {
  Currency,
  type GetAllPaymentItemsQuery,
  Interval,
  type ItemPrice,
  type PaymentItemPromotion,
  PaymentItemType,
  type PriceWithCurrency,
} from '../apollo/generated/graphql'
import {
  executeCancelSubscription,
  executeResumeSubscription,
  fetchGetMyMembership,
  refetchGetMyMembership,
  updateGetMyMembership,
  useMyMembership,
} from '../apollo/membership'
import { useGetAllPaymentItems } from '../apollo/payment'
import {
  handleChangePlanSuccess,
  handlePaymentError,
  isMobilePaymentProvider,
  PaymentProvider,
} from './payment'

export type MembershipPlanPrices = Record<
  Interval,
  Record<Currency, MembershipParsedPrices>
>

type MembershipParsedPrices = Record<
  | 'perMonth'
  | 'perYear'
  | 'perMonthOriginal'
  | 'perYearOriginal'
  | 'perWeek'
  | 'perDay',
  number
>

export type MembershipPlan = {
  id: string
  planId?: string
  oneTimeMembership?: MembershipPlan
  tier: number
  prices: MembershipPlanPrices
  privileges: MembershipPlanPrivileges
  promotions?: PaymentItemPromotion[]
}

// Copied from packages/pixai-server/src/service/helper/payment/itemConfig/type.ts
// Keep it in sync when change the corresponding type in the server
type MembershipPlanPrivileges = {
  paidCredit?: number
  dailyClaimAdded?: number
  extraPackageValue?: number
  note?: number
  cover?: string // allow custom cover
  privateModel?: number // allowed private model num
  bulkGen?: boolean
  moreSize?: boolean
  lora?: number // max lora num
  freeUserLora?: number
  professionalMode?: boolean
}

function mergeCurrencies(
  original: PriceWithCurrency[],
  discounted?: PriceWithCurrency[],
) {
  if (!discounted) return original
  return original.map(price => ({
    ...price,
    ...discounted.find(i => i.currency === price.currency),
  }))
}

function mapPlanPrices(
  prices: ItemPrice[],
  discountedPrices?: PriceWithCurrency[],
) {
  return Object.fromEntries(
    prices.map(({ interval, currencies }) => [
      interval,
      Object.fromEntries(
        (interval === 'MONTH'
          ? mergeCurrencies(currencies, discountedPrices)
          : currencies
        ).map(price => priceMapFnForMembershipPlans(price, interval)),
      ),
    ]),
  )
}

/**
 * Get all membership plans
 *
 * @param opts.discountedMonthlyPrices Optional discount override map, e.g.
 *   `{ 'membership-start': { currencies: [...] } }`.
 * @param opts.initialPaymentItems Optional SSR-seeded payment items payload
 *   used as a fallback while `useGetAllPaymentItems` has not yet resolved.
 *   The Landing route preloads this via `fetchPaymentItemsForSSR` so the
 *   server-rendered HTML can ship real prices without waiting for the
 *   client-side `no-cache` Apollo query.
 * @returns The normalized data of all available plans
 */
export function useMembershipPlans(opts?: {
  discountedMonthlyPrices?: Record<string, { currencies: PriceWithCurrency[] }>
  initialPaymentItems?: GetAllPaymentItemsQuery['paymentItems']
}): MembershipPlan[] {
  const { data: paymentItems } = useGetAllPaymentItems({})
  const effectivePaymentItems = paymentItems ?? opts?.initialPaymentItems

  return useMemo(
    () =>
      compact(
        effectivePaymentItems?.map(plan => {
          return isMatching(
            {
              type: PaymentItemType.Plan,
              tier: P.number,
            },
            plan,
          )
            ? {
                id: plan.id,
                planId: plan.planId,
                tier: plan.tier,
                privileges: plan.privilege,
                /**
                 * @example
                 *   {
                 *     MONTH: {
                 *       USD: {
                 *         perMonth: 9.99,
                 *         perYear: 119.88
                 *       }
                 *     },
                 *     YEAR: {
                 *       USD: {
                 *         perMonth: 8.33,
                 *         perYear: 99.96
                 *       }
                 *     }
                 *   }
                 */
                prices: mapPlanPrices(
                  plan.prices,
                  opts?.discountedMonthlyPrices?.[plan.id]?.currencies,
                ),
                oneTimeMembership: (() => {
                  const oneTimeMembership = effectivePaymentItems?.find(
                    p => p.planId === plan.id && p.tier === plan.tier,
                  )
                  if (!oneTimeMembership || !oneTimeMembership.tier) return

                  return {
                    id: oneTimeMembership.id,
                    tier: oneTimeMembership.tier,
                    privileges: oneTimeMembership.privilege,
                    prices: mapPlanPrices(
                      oneTimeMembership.prices,
                      opts?.discountedMonthlyPrices?.[plan.id]?.currencies,
                    ),
                  }
                })(),
                promotions: plan.promotions,
              }
            : null
        }),
      ),
    [opts?.discountedMonthlyPrices, effectivePaymentItems],
  )
}

function priceMapFnForMembershipPlans(
  price: PriceWithCurrency,
  interval?: Interval,
) {
  const normalizedPrice = normalizePrice(price)
  const normalizedOriginalPrice = normalizePrice({
    amount: price.originalAmount || price.amount,
    by: price.by,
  })

  // API always returns the billing price, i.e. all-year price for annual
  // subscription. Therefore, we need to calculate the monthly price.
  const calculatedPrices =
    interval === Interval.Month
      ? {
          perMonth: normalizedPrice,
          perYear: normalizedPrice,
          // Enforced discountRatio won't affect original price
          perMonthOriginal: normalizedOriginalPrice,
          perYearOriginal: normalizedOriginalPrice * 12,
          perWeek: normalizedPrice / 4,
          perDay: normalizedPrice / 30,
        }
      : {
          // Round to necessary precision
          perMonth: normalizePriceForYearPlan({
            amount: normalizedPrice,
            by: price.by,
            per: 'month',
          }),
          perWeek: normalizePriceForYearPlan({
            amount: normalizedPrice,
            by: price.by,
            per: 'week',
          }),
          perDay: normalizePriceForYearPlan({
            amount: normalizedPrice,
            by: price.by,
            per: 'day',
          }),
          perYear: normalizedPrice,
          perMonthOriginal: normalizePriceForYearPlan({
            amount: normalizedOriginalPrice,
            by: price.by,
            per: 'month',
          }),
          perYearOriginal: normalizedOriginalPrice,
        }
  return [price.currency, calculatedPrices]
}

/**
 * Get the specific membership plan by plan ID
 *
 * @param planId - The ID of the plan
 * @returns The normalized data of the plan, or `undefined` if no matched
 */
export function useMembershipPlan(planId?: string): MembershipPlan | undefined
/**
 * Get the specific membership plan by tier number
 *
 * @param tier - The tier number of the plan
 * @returns The normalized data of the plan, or `undefined` if no matched
 */
export function useMembershipPlan(tier?: number): MembershipPlan | undefined
// Implementation
export function useMembershipPlan(
  planIdOrTier?: string | number,
): MembershipPlan | undefined {
  const plans = useMembershipPlans()
  if (planIdOrTier) {
    return plans.find(it =>
      typeof planIdOrTier === 'number'
        ? it.tier === planIdOrTier
        : it.id === planIdOrTier || it.planId === planIdOrTier,
    )
  }
}

// oxfmt-ignore
function normalizePrice({ amount, by }: Pick<PriceWithCurrency, 'amount' | 'by'>) {
  return amount / by
}

/**
 * Cosmetic only
 * Won't be used in actual purchase
 * @param param0
 * @returns
 */
function normalizePriceForYearPlan({
  amount,
  by,
  per,
}: Pick<PriceWithCurrency, 'amount' | 'by'> & {
  per: 'day' | 'month' | 'week'
}) {
  const dividedBy = match(per)
    .with('day', () => 365)
    .with('month', () => 12)
    .otherwise(() => 52)
  return round(amount / dividedBy, Math.log10(by))
}

export type MembershipSubscribeActionArgs = {
  /**
   * Params to append on new subscription / upgrade redirect
   */
  extraParamsForSubscribeRedirection?: Record<string, any>
  /**
   * After payment cancellation
   */
  afterCancelSurvey?: () => any
}

/**
 * Get helper functions for membership actions
 */
export function useMembershipActions() {
  const { data: { membership, subscription } = {} } = useMyMembership()
  const { isUnknownProvider } = useSubscriptionInfo()
  const [currency] = useCurrency()
  const matches = useMatches()
  const navigate = useNavigate()
  const scene = (matches.at(-1)?.handle as { name?: string })?.name

  return useMemo(
    () => ({
      /**
       * Subscribe to a plan (subscribe, resubscribe, upgrade, downgrade)
       *
       * @returns `boolean` indicating whether the subscription is successful.
       *          `void` when a redirection is needed.
       */
      async subscribe(
        args: {
          isActive: boolean
          planId: string
          oneTimeMembership?: MembershipPlan
          tier: number
          prices: Record<Currency, number>
          interval: Interval
          isNewSubscribe?: boolean
          /**
           * Explicitly telling server to apply discount
           */
          shouldDiscount?: boolean
          trackingOpts?: {
            upgradeFromTier?: number
            discountType?: string
          }
        } & MembershipSubscribeActionArgs,
      ) {
        // When using unknown payment service provider (e.g. Mobile IAP), Web
        // purchase is prohibited
        if (isUnknownProvider) return

        const {
          planId = missingArgumentError('planId'),
          interval = missingArgumentError('interval'),
          oneTimeMembership,
        } = args

        const subscribeType = match({
          wasActive: args.isActive,
          tierDiff: args.tier - (membership?.tier ?? Number.NaN),
          subscription,
        })
          .with({ tierDiff: Number.NaN }, () => 'subscribe')
          .with({ tierDiff: 0, wasActive: false }, () => 'resubscribe')
          .with({ tierDiff: 0, wasActive: true }, () => 'extend')
          .with({ tierDiff: P.number.positive() }, () => 'upgrade')
          .with({ tierDiff: P.number.negative() }, () => 'downgrade')
          .otherwise(() => null)

        if (!subscribeType) return

        // Main logic
        try {
          track('begin_checkout', undefined)

          const { finalizedPaymentProvider } = await openPaymentDialog({
            itemId: planId,
            oneTimeMembership,
            interval,
            currency,
            prices: args.prices,
            paymentProvider: {
              allowed: 'any',
            },
            redirect: new URL(
              withQuery('/payment/subscription/landing', {
                itemId: planId,
                interval,
                currency,
                discountType: args.trackingOpts?.discountType,
                price: args.prices[currency],
                usdPrice: args.prices[Currency.Usd],
                ...args.extraParamsForSubscribeRedirection,
              }),
              window.location.origin,
            ).toString(),
            dialogTrackingOpts: {
              scene: (matches.at(-1)?.handle as { name?: string })?.name,
            },
            shouldDiscount: args.shouldDiscount,
            afterCancelSurvey: args.afterCancelSurvey,
          })

          // Navigate to payment success page manually for payment providers that
          // shows an in-site dialog (currently all providers show in-site dialog instead
          // of navigating off-site)
          if (
            (finalizedPaymentProvider === PaymentProvider.braintree ||
              finalizedPaymentProvider === PaymentProvider.onerway ||
              finalizedPaymentProvider ===
                PaymentProvider.onerwayJcbAeUnionPay ||
              finalizedPaymentProvider === PaymentProvider.paypay) &&
            args.isNewSubscribe
          ) {
            return navigate(
              withQuery('/payment/subscription/landing', {
                itemId: planId,
                interval,
                currency,
                usdPrice: args.prices[Currency.Usd],
                ...args.extraParamsForSubscribeRedirection,
              }),
            )
          }

          const newMembership = await fetchGetMyMembership({})
          // oxfmt-ignore
          const isActive = newMembership.data?.me?.subscription?.status === UserSubscriptionStatus.active

          // track "succeeded" membership payments
          // skip tracking this if it is a new subscription
          if (!args.isNewSubscribe && isActive) {
            track('order_payment_success', {
              type: interval,
              cate: subscribeType,
              number: args.tier,
              price: args.prices[currency],
              usdPrice: args.prices[Currency.Usd],
              currency,
              preplan: args.trackingOpts?.upgradeFromTier,
              provider: finalizedPaymentProvider,
              scene,
            })
          }

          // Only redirect the user when it's not resubscribing
          if (subscribeType === 'resubscribe') {
            endEventGroup('member_source')
          } else {
            if (isActive) {
              return navigate(
                withQuery('/payment/subscription/result', {
                  type: subscribeType,
                  ...args.extraParamsForSubscribeRedirection,
                }),
              )
            }
          }
          if (newMembership.data) {
            updateGetMyMembership(newMembership.data)
          }
          return isActive
        } catch (err) {
          handlePaymentError(err)
        }
      },

      /**
       * Unsubscribe from the current plan
       */
      async unsubscribe() {
        try {
          await openCancelSubscriptionDialog(subscription, membership?.tier)
          await executeCancelSubscription({})
          await handleChangePlanSuccess()
          navigate('/payment/subscription/result?type=cancel')
        } catch (err) {
          if (err === UserCancelled) return
          handlePaymentError(err)
        }
      },

      /**
       * Resume the auto-renewal of the subscription
       * Not available for all payment providers
       *
       */
      async resume() {
        try {
          await executeResumeSubscription({})
          refetchGetMyMembership()
          navigate('/payment/subscription/result?type=resume')
        } catch (err) {
          if (err === UserCancelled) return
          handlePaymentError(err)
        }
      },
    }),
    [
      membership,
      subscription,
      isUnknownProvider,
      currency,
      navigate,
      matches,
      scene,
    ],
  )
}

/**
 * Get the subscription info of the current user
 */
export function useSubscriptionInfo() {
  const { data } = useMyMembership()

  const subscription =
    data?.subscription?.status === UserSubscriptionStatus.inactive
      ? undefined
      : data?.subscription

  return useMemo(
    () => ({
      isActive: subscription?.status === UserSubscriptionStatus.active,
      isStripe: subscription?.provider === PaymentProvider.stripe,
      isPaddle: subscription?.provider === PaymentProvider.paddle,
      isBraintree: subscription?.provider === PaymentProvider.braintree,
      isOnerway: subscription?.provider === PaymentProvider.onerway,
      isMobileProvider: isMobilePaymentProvider(subscription?.provider),
      isUnknownProvider: !isMatching(
        P.union(
          PaymentProvider.stripe,
          PaymentProvider.paddle,
          PaymentProvider.braintree,
          PaymentProvider.onerway,
          PaymentProvider.paypay,
          P.nullish,
        ),
        subscription?.provider,
      ),
      isManageableProvider: false, // No provider is manageable for now, including onerway
      isUpgradeDowngradeSupportedProvider: isMatching(
        P.union(
          PaymentProvider.stripe,
          PaymentProvider.paddle,
          PaymentProvider.braintree,
          // FIXME(toki): Do not allow to upgrade/downgrade before billing system finishes, or there is a discount event
          PaymentProvider.onerway,
          PaymentProvider.paypay,
          PaymentProvider.apple,
          PaymentProvider.google,
        ),
        subscription?.provider,
      ),
    }),
    [subscription],
  )
}

/**
 * Growth:
 * If user has chosen 3+ loras, open membership dialog
 * Triggers once per day
 */
export function useHasOpenedMembershipForExcessiveLoras() {
  const [lastClose, setLastClose] = useLocalStorage({
    key: 'growth:last-opened-membership-for-excessive-loras',
    defaultValue: 0,
  })

  const updateLastClose = useCallback(() => {
    setLastClose(Date.now())
  }, [])

  const shouldOpen = useMemo(() => {
    const todayClosed = dayjs(lastClose).isAfter(dayjs().startOf('day'))
    return !todayClosed
  }, [lastClose])

  return [shouldOpen, updateLastClose] as const
}
